/**
 * The dashboard arrangement.
 *
 * Anyone can rearrange the dashboard: drag a tile where they want it, drop it
 * into another section, or hide what they do not use. The arrangement belongs
 * to the person looking at it, so it is kept apart from the data and can be
 * reset or exported without touching anything the airport actually recorded.
 *
 * Hand-ported: the original keeps this in a module-level global that the boards
 * read directly, and the extracted domain reads it by name.
 */

export interface Layout {
  /** tile key -> tier it has been moved to, plus "_order<tier>" arrays */
  tiers: Record<string, unknown>;
  /** tile key -> hidden */
  hidden: Record<string, boolean>;
  saved: boolean;
}

export const LAYOUT: Layout = { tiers: {}, hidden: {}, saved: false };

/*
 * TILESTYLE is deliberately NOT exported from here. The demo binds it in one
 * statement with DASHVIEW and PAXCHART:
 *
 *   let DASHVIEW = "overview", TILESTYLE = "instrument", PAXCHART = "columns";
 *
 * so providing it from this module makes the extractor skip that statement and
 * strands the other two as undefined globals. It is owned by the generated
 * slice; boards that need to switch presentation hold it as their own state.
 */
export type TileStyle = "instrument" | "card" | "list";

const KEY = "skylogics.dashboard.layout";

/** Returns the saved tile style, if there was one — the caller owns that state. */
export function layoutLoad(): TileStyle | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const o = JSON.parse(raw);
      LAYOUT.tiers = o.tiers || {};
      LAYOUT.hidden = o.hidden || {};
      LAYOUT.saved = true;
    }
    return raw ? (JSON.parse(raw).tiles ?? null) : null;
  } catch {
    /* storage unavailable — the layout simply lasts for this session */
    return null;
  }
}

export function layoutSave(tileStyle: TileStyle = "instrument"): void {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ tiers: LAYOUT.tiers, hidden: LAYOUT.hidden, tiles: tileStyle }),
    );
    LAYOUT.saved = true;
  } catch {
    LAYOUT.saved = false;
  }
}

/** The order for a tier: whatever the user set, then anything new appended. */
export function tierOrder<T extends { key: string; tier: number; crit: number }>(
  tier: number,
  cards: T[],
): T[] {
  const mine = cards.filter((c) =>
    (LAYOUT.tiers[c.key] != null ? LAYOUT.tiers[c.key] : c.tier) === tier,
  );
  const saved = ((LAYOUT.tiers["_order" + tier] as string[]) || []).filter((k) =>
    mine.some((c) => c.key === k),
  );
  const rest = mine
    .filter((c) => !saved.includes(c.key))
    .sort((a, b) => a.crit - b.crit)
    .map((c) => c.key);
  return saved
    .concat(rest)
    .map((k) => mine.find((c) => c.key === k))
    .filter(Boolean) as T[];
}
